import isNil from 'lodash/isNil';
import {Observable} from 'rxjs/Observable';
import {Subject} from 'rxjs/Subject';
import {AuthCacheService} from '../../auth/services/AuthCacheService';
import {isCordovaBuild} from '../../config/constants';
import {UploadStatus} from './UPLOAD_STATUS';


export class FileDataCacheService {
  static SERVICE_NAME = 'fileDataCacheService';
  static COUNTER_KEY = 'fileDataCacheService_COUNTER';
  static FILES_KEY = 'fileDataCacheService_FILES';

  static $inject = [
    AuthCacheService.SERVICE_NAME,
    '$persistentCacheFactory',
  ];

  constructor(
    authCacheService,
    $persistentCacheFactory,
  ) {
    /** @private */
    this.authCacheService = authCacheService;
    /** @private */
    this.$persistentCacheFactory = $persistentCacheFactory;

    /** @private */
    this.countSubject = new Subject();
    /** @private */
    this.filesSubject = new Subject();
    /** @private */
    this.initComplete = false;
    /** @private */
    this.initialized = null;
  }

  init() {
    if (this.initialized) {
      return this.initialized;
    }

    if (isCordovaBuild()) {
      this.initialized = this.initCordova();
    } else {
      this.initialized = this.initWeb({});
    }

    return this.initialized;
  }

  /**
   * @return {Promise<T | never> | *}
   * @private
   */
  initCordova() {
    /** @private */
    return Observable
      .merge(
        // timeout after 30 seconds and give up of fetching local cache.
        Observable.timer(30000).mapTo({}),
        this.initialized = this.migrateExistingCache()
          .then(migration =>
            migration.migrated
              ? migration
              : Promise.props({
                counter: this.authCacheService.getItem(FileDataCacheService.COUNTER_KEY),
                files: this.getFilesFromCache(),
              })
          ),
      )
      .take(1)
      .toPromise()
      .then(data => this.initWeb(data));
  }

  /**
   * @param data
   * @return {Promise<T | never> | *}
   * @private
   */
  initWeb(data) {
    this.initComplete = true;

    /**
     * @type {Number}
     * @private
     */
    this.counter = data.counter || 0;
    /**
     * @type {Object}
     * @private
     */
    this.files = data.files || {};

    /** @private */
    this.countUpdateSubscription = this.buildSubscription(
      this.countSubject,
      FileDataCacheService.COUNTER_KEY,
      'counter',
    );
    /** @private */
    this.countUpdateSubscription = this.buildSubscription(
      this.filesSubject,
      FileDataCacheService.FILES_KEY,
      'files',
    );

    return Promise.resolve();
  }

  waitForInitialization() {
    return this.initialized;
  }

  isInitialized() {
    return this.initComplete;
  }

  getFileCache() {
    return this.files;
  }

  generateKey() {
    this.counter++;
    this.countSubject.next();
    return this.counter;
  }

  updateFileCache(files) {
    this.files = files;
    this.filesSubject.next();
  }

  /**
   * @typedef {Object} FileDataCacheService#MigrationResult
   * @property {Number} counter
   * @property {Object} files
   * @property {Boolean} migrated
   */

  /**
   * @return {Promise<FileDataCacheService#MigrationResult>}
   * @private
   */
  migrateExistingCache() {
    const FILE_STORE_KEY = 'persistent-file-uploader-cache';
    const FILE_DATA_KEY = `${FILE_STORE_KEY}-FILE_DATA`;
    const COUNTER_KEY = `${FILE_STORE_KEY}-COUNTER`;
    const cache = this.$persistentCacheFactory.get(FILE_STORE_KEY) || this.$persistentCacheFactory(FILE_STORE_KEY, {
      useLocalStorage: isCordovaBuild(),
    });

    const fileCache = cache.get(FILE_DATA_KEY);
    const counter = cache.get(COUNTER_KEY);
    const fileCacheSize = !isNil(fileCache) ? Object.keys(fileCache).length : 0;
    const hasFileCache = fileCacheSize > 0;
    const hasCounter = !isNil(counter);

    if (!hasFileCache && !hasCounter) {
      return Promise.resolve({
        migrated: false,
      });

    } else {
      const promises = {};
      const files = hasFileCache ? FileDataCacheService.validateCacheIntegrity(fileCache) : null;

      if (hasFileCache) {
        promises.files = this.authCacheService.setItem(FileDataCacheService.FILES_KEY, files);
      }

      if (hasCounter) {
        promises.counter = this.authCacheService.setItem(FileDataCacheService.COUNTER_KEY, counter);
      }

      return Promise
        .props(promises)
        .then(() => {
          cache.remove(FILE_DATA_KEY);
          cache.remove(COUNTER_KEY);
          return {
            counter,
            files,
            migrated: true
          };
        });
    }
  }

  /**
   * @return {Promise<Object>}
   * @private
   */
  getFilesFromCache() {
    return this.authCacheService
      .getItem(FileDataCacheService.FILES_KEY)
      .then(FileDataCacheService.validateCacheIntegrity)
  }

  /**
   * Build a rate limited save-to-cache subscription.
   * @param {Subject} subject
   * @param {String} key
   * @param {String} prop
   * @return {Subscription}
   * @private
   */
  buildSubscription(subject, key, prop) {
    if (isCordovaBuild()) {
      return subject
        .throttleTime(1000, void 0, {leading: true, trailing: true})
        .subscribe(() => {
          this.authCacheService.setItem(key, this[prop]);
        });
    }
  }

  /**
   * TODO: May want to detect and automatically retry uploads if any UploadStatus.UPLOADED exist
   * @param {Object} files
   * @return {Object}
   * @private
   */
  static validateCacheIntegrity(files) {
    for (const key in files) {
      if (files.hasOwnProperty(key) && files[key]) {
        const file = files[key];
        delete file.noteId; // clear any previous note IDs
        if (file.uploadStatus === UploadStatus.UPLOADING) {
          file.uploadStatus = UploadStatus.PENDING;
        }
      }
    }
    return files;
  }
}



// WEBPACK FOOTER //
// ./src/js/core-utilities/upload-service/FileDataCacheService.js